// Tokens are held in memory only — never persisted to localStorage.
// The HttpOnly cookie set by the server survives page reloads; on refresh
// we re-authenticate via fetchMe() using that cookie.
let _memToken: string | null = null;

export function getToken(): string | null {
  return _memToken;
}

export function setToken(token: string): void {
  _memToken = token;
}

export function clearToken(): void {
  _memToken = null;
  stopTokenRefresh();
}

// ─── Silent token refresh ──────────────────────────────────────────────────
// JWT expires in 1 hour; refresh every 50 minutes so the session stays alive
// as long as the tab is open.
let _refreshTimer: ReturnType<typeof setInterval> | null = null;
const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes

async function doRefresh(onNewToken?: (t: string) => void): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) {
      const data = await res.json() as { token?: string };
      if (data.token) {
        _memToken = data.token;
        onNewToken?.(data.token);
      }
    }
  } catch {
    // Silent — the cookie may still be valid for the next attempt
  }
}

/**
 * Begin periodic silent token refresh. Call once after login/auth restore.
 * `onNewToken` is invoked with the fresh JWT so the caller can update React state.
 */
export function startTokenRefresh(onNewToken?: (t: string) => void): void {
  stopTokenRefresh();
  _refreshTimer = setInterval(() => doRefresh(onNewToken), REFRESH_INTERVAL_MS);
}

export function stopTokenRefresh(): void {
  if (_refreshTimer) {
    clearInterval(_refreshTimer);
    _refreshTimer = null;
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort — clear local state regardless
  }
  clearToken();
}

// Shared user-facing error message for network / fetch failures.
// See plan: 2026-05-24 Phase 2 Task 4 Refinement 1 — DRY the 9 identical
// "Network error" strings into one constant.
const NETWORK_ERROR_MESSAGE = "Couldn't reach the server. Check your connection and try again.";

export interface GuideCustomization {
  characterId: string;
  auraId: string | null;
  equippedItems: string[];
  pinIds: string[];
  wornIds: string[];
}

export interface UserProfile {
  id: number;
  email: string;
  participantId?: string;
  isAdmin?: boolean;
  isModerator?: boolean;
  displayName: string | null;
  pronouns: string | null;
  ageRange: string | null;
  birthYear: number | null;
  province: string | null;
  scdType: string | null;
  diagnosisYear: number | null;
  careCenter: string | null;
  carePreferences: string[] | null;
  avatarId: string | null;
  avatarEmoji: string | null;
  avatarCustomisation: GuideCustomization | null;
  guideBondProgress: Record<string, number> | null;
  customChips: Record<string, string[]> | null;
  hobbies: string | null;
  specialInterests: string | null;
  healthTeamNote: string | null;
  completedOnboarding: boolean;
  identities: string[];
}

const API_BASE = '/api';

/**
 * Ping to wake Neon from suspension. Returns a promise that resolves once the
 * DB is reachable OR after 16s (whichever comes first). The timeout prevents
 * the entire UI from freezing if Neon takes a long time to wake — subsequent
 * API calls will hit the DB directly and either succeed (Neon woke up) or
 * retry via apiFetch/fetchWithRetry. Deduplicates concurrent callers.
 */
let _warmPromise: Promise<void> | null = null;
export function warmBackend(): Promise<void> {
  if (!_warmPromise) {
    const warmFetch = fetch(`${API_BASE}/warm`).catch(() => {});
    const timeout = new Promise<void>(r => setTimeout(r, 12_000));
    _warmPromise = Promise.race([warmFetch, timeout])
      .then(() => { _warmPromise = null; });
  }
  return _warmPromise;
}

/**
 * Wait for the DB warm-up, then fetch. Retries once on 503 (Neon cold-start).
 * Exported so any API call in the app can use it for cold-start resilience.
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // If a warm-up is in flight, wait for it so we don't race against the DB.
  if (_warmPromise) await _warmPromise;
  const res = await fetch(input, init);
  if (res.status === 503) {
    const wait = parseInt(res.headers.get('Retry-After') || '3', 10);
    await new Promise(r => setTimeout(r, wait * 1000));
    return fetch(input, init);
  }
  return res;
}

async function parseJsonResponse(res: Response): Promise<Record<string, unknown>> {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch { /* malformed data */
    return {};
  }
}

export async function fetchMe(token?: string | null): Promise<UserProfile | null> {
  try {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await apiFetch(`${API_BASE}/auth/me`, {
      headers,
      credentials: 'include',
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { /* request or JSON parse failed */
    return null;
  }
}

export type RegisterResult =
  | { requiresSignupVerification: true; pendingToken: string }
  | { token: string }
  | { error: string };

export async function register(
  email: string,
  password: string,
  displayName: string,
  recaptchaToken?: string
): Promise<RegisterResult> {
  let res: Response;
  try {
    res = await apiFetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, displayName, recaptchaToken }),
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const data = await parseJsonResponse(res);
  if (!res.ok) {
    if (res.status === 409) return { error: (data.error as string) || 'An account with this email already exists.' };
    if (res.status === 429) return { error: 'Too many attempts. Please wait a few minutes and try again.' };
    return { error: (data.error as string) || "Something didn't go through. Try again in a moment." };
  }
  if (data.requiresSignupVerification) {
    return { requiresSignupVerification: true, pendingToken: data.pendingToken as string };
  }
  if (data.token) {
    return { token: data.token as string };
  }
  return { error: "The server sent something we didn't expect. Try again — if it keeps happening, let us know." };
}

export type LoginResult =
  | { token: string }
  | { requires2fa: true; pendingToken: string }
  | { error: string };

export async function login(
  email: string,
  password: string,
  recaptchaToken?: string,
  rememberMe?: boolean
): Promise<LoginResult> {
  let res: Response;
  try {
    res = await apiFetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, recaptchaToken, rememberMe }),
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const data = await parseJsonResponse(res);
  if (!res.ok) return { error: (data.error as string) || `Login failed (${res.status})` };
  if (data.requires2fa) return { requires2fa: true, pendingToken: data.pendingToken as string };
  return { token: data.token as string };
}

export async function requestDevCode(): Promise<{ ok: true } | { error: string }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/code-login/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const data = await parseJsonResponse(res);
  if (!res.ok) return { error: (data.error as string) || `Failed to send code (${res.status})` };
  return { ok: true };
}

export async function codeLogin(
  code: string
): Promise<{ token: string } | { error: string }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/code-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const data = await parseJsonResponse(res);
  if (!res.ok) return { error: (data.error as string) || `Invalid code (${res.status})` };
  return { token: data.token as string };
}

export async function sendDevCode(
  destination: string
): Promise<{ ok: true } | { error: string }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/dev-code/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ method: 'email', destination }),
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const data = await parseJsonResponse(res);
  if (!res.ok) return { error: (data.error as string) || `Failed to send code (${res.status})` };
  return { ok: true };
}

export async function verifyDevCode(
  code: string,
  destination?: string
): Promise<{ ok: true; token?: string; user?: { id: number; email: string; participantId: string } } | { error: string }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/dev-code/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(destination ? { code, destination } : { code }),
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const data = await parseJsonResponse(res);
  if (!res.ok) return { error: (data.error as string) || `Verification failed (${res.status})` };
  return {
    ok: true,
    token: typeof data.token === 'string' ? data.token : undefined,
    user: data.user as { id: number; email: string; participantId: string } | undefined,
  };
}

export async function verify2fa(
  pendingToken: string,
  code: string
): Promise<{ token: string } | { error: string }> {
  let res: Response;
  try {
    res = await apiFetch(`${API_BASE}/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ pendingToken, code }),
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const rawText = await res.text();
  let data: Record<string, unknown> = {};
  try { data = JSON.parse(rawText); } catch { /* not JSON */ }
  if (!res.ok) {
    const serverError = data.error as string | undefined;
    if (res.status >= 500 && !serverError) {
      const preview = rawText.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 200);
      console.error(`verify2fa server error (${res.status}):`, rawText.slice(0, 500));
      return { error: "Verification didn't go through. Try the code again, or request a new one." };
    }
    return { error: serverError || `Verification failed (${res.status})` };
  }
  if (typeof data.token !== 'string' || !data.token) {
    return { error: "Verification went through, but we couldn't sign you in. Try again." };
  }
  return { token: data.token };
}

export async function forgotPassword(
  email: string
): Promise<{ ok: true } | { error: string }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const data = await parseJsonResponse(res);
  if (!res.ok) return { error: (data.error as string) || `Request failed (${res.status})` };
  return { ok: true };
}

export async function resetPassword(
  token: string,
  newPassword: string,
  email: string
): Promise<{ ok: true } | { error: string }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token, newPassword, email }),
    });
  } catch { /* network request failed */
    return { error: NETWORK_ERROR_MESSAGE };
  }
  const data = await parseJsonResponse(res);
  if (!res.ok) return { error: (data.error as string) || `Reset failed (${res.status})` };
  return { ok: true };
}

export async function updateProfile(
  token: string | null | undefined,
  updates: Partial<Omit<UserProfile, 'id' | 'email'>>
): Promise<boolean | string> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await apiFetch(`${API_BASE}/auth/profile`, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return (data as any).error || `Couldn't save your profile (${res.status}). Try again.`;
    }
    return true;
  } catch (err) {
    console.error('updateProfile error:', err);
    return "Couldn't save your profile right now. Check your connection and try again.";
  }
}

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

/** Dynamically loads the reCAPTCHA script on first use (only needed for auth). */
let recaptchaLoadPromise: Promise<void> | null = null;
function ensureRecaptchaLoaded(): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();
  if (recaptchaLoadPromise) return recaptchaLoadPromise;
  recaptchaLoadPromise = new Promise<void>((resolve, reject) => {
    if (!RECAPTCHA_SITE_KEY) { resolve(); return; }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { recaptchaLoadPromise = null; reject(); };
    document.head.appendChild(script);
  });
  return recaptchaLoadPromise;
}

export async function getRecaptchaToken(action: string): Promise<string | undefined> {
  if (!RECAPTCHA_SITE_KEY) return undefined;
  try { await ensureRecaptchaLoaded(); } catch { return undefined; }
  return new Promise(resolve => {
    if (!window.grecaptcha) { resolve(undefined); return; }
    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha!.execute(RECAPTCHA_SITE_KEY, { action });
        resolve(token);
      } catch { /* recaptcha execution failed, non-critical */
        resolve(undefined);
      }
    });
  });
}
